import { Box, Card, CardContent, Button, Grid, Typography } from "@mui/material";
import React, { useState, ChangeEvent, useRef, useEffect } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import api from "../../services/ApiService";
import { ApiResponse } from "../../models/ApiResponse";
import { toast } from "react-toastify";
import * as XLSX from 'xlsx';
import InfoIcon from '@mui/icons-material/Info';
import FileDownload from "../../components/FileDownload";
import Loader from "../../components/Loader";
import { XLSX_MIME_TYPE } from "./EditItem/Constants";
import ToggleSwitchButton from "../../components/ToggleSwitchButton";
import SessionService from "../../services/SessionService";
import { User } from "../../models/User";

const BulkUpdate = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileData, setFileData] = useState<any[]>([]);
  const [columnData, setColumnData] = useState<any[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [applySkuValidation, setApplySkuValidation] = useState<boolean>(true);
  const [canBypassSkuValidation, setCanBypassSkuValidation] = useState<boolean>(false);
  const sessionService = SessionService.getInstance();

  useEffect(() => {
    const userId = sessionService.getCurrentUser()?.id;
    if (!userId) return;

    api
      .get<ApiResponse<User>>(`/UserRole/${userId}`)
      .then((response: ApiResponse<User>) => {
        if (response.isSuccess) {
          setCanBypassSkuValidation(!!response.data?.canBypassSkuValidation);
        }
      })
      .catch((error) => {
        console.error("Exception from UserRole", error);
      });
  }, []);

  const handleFileChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    setSelectedFile(file);
  };

  const handleClear = () => {
    setColumnData([]);
    setFileData([]);
    setIsError(false);
    setSelectedFile(null);
    fileInputRef.current.value = "";
  };
  const fileInputRef = useRef(null);

  const handleBrowseClick = () => {
    setColumnData([]);
    setFileData([]);
    setIsError(false);
    fileInputRef.current.click();
  };
  const handleSubmit = async () => {
    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
    const formData = new FormData();
    if (selectedFile?.type !== XLSX_MIME_TYPE) {
      return toast.error("Only XLSX files are supported.");
    }
    if (selectedFile.size < maxSizeInBytes) {
      formData.append("file", selectedFile); // Assuming 'file' is the key expected by the server
      api
        .put<ApiResponse<any>>("/Item/bulk-update", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          params: {
            applySkuValidation,
          },
        })
        .then((response) => {
          if (response.isSuccess) {
            toast.success(response.message);

            if (response?.data?.misMatchedItemIDs?.length > 0) {
              const reader = new FileReader();

              reader.onload = (e) => {
                const result = e.target?.result;
                if (!(result instanceof ArrayBuffer)) {
                  console.error("Invalid file format");
                  return;
                }
                const data = new Uint8Array(result);
                const workbook = XLSX.read(data, { type: 'array' });

                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rowData: any[] = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });

                const filteredArray = rowData?.filter((item) =>
                  response?.data?.misMatchedItemIDs?.includes(Number(item?.PrjCode))
                );

                const columns = filteredArray?.length
                  ? Object.keys(filteredArray[0]).map((field) => ({
                    field: field === 'PrjCode' ? 'id' : field,
                    headerName: field,
                    width: 160,
                  }))
                  : [];

                const convertedData = filteredArray?.map((item, index) => {
                  const updatedItem = { ...item, id: item?.PrjCode || index + 1 };
                  delete updatedItem.Id;
                  return updatedItem;
                }) || [];

                if (response?.data?.recordsNotUpdated > 0) {
                  setErrorMessage('Below fields cannot be updated');
                  setIsError(true);
                  setSelectedFile(null);
                } else {
                  setSelectedFile(null);
                  setIsError(false);
                }

                fileInputRef.current.value = '';
                setColumnData(columns);
                setFileData(convertedData);
                reader.readAsArrayBuffer(selectedFile);
              };
            } else {
              setSelectedFile(null);
              fileInputRef.current.value = '';
            }
          }
        })
        .catch((error) => {
          const errorResponse = error?.response?.data;

          const invalidMultiSelectFieldErrors = errorResponse?.data?.invalidMultiSelectFieldErrors as
            | Record<string, string[]>
            | undefined;

          const hasInvalidMultiSelectFieldErrors =
            !!invalidMultiSelectFieldErrors && Object.keys(invalidMultiSelectFieldErrors).length > 0;

          if (
            errorResponse?.errors?.length > 0 ||
            errorResponse?.data?.skuValidationErrors?.length > 0 ||
            hasInvalidMultiSelectFieldErrors
          ) {
            const reader = new FileReader();

            reader.onload = (e) => {
              const result = e.target?.result;
              if (!(result instanceof ArrayBuffer)) {
                console.error("Invalid file format");
                return;
              }

              const data = new Uint8Array(result);
              const workbook = XLSX.read(data, { type: 'array' });

              const sheet = workbook.Sheets['Products'];
              const rowData: any[] = sheet
                ? XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false })
                : []; // header inferred automatically

              // 1. Handle SKU Validation Errors
              if (errorResponse?.data?.skuValidationErrors?.length > 0) {
                const skuErrorsData = errorResponse.data.skuValidationErrors.map((item: any, index: number) => ({
                  ...item,
                  id: index + 1,
                }));

                const skuColumns = [
                  { field: "code", headerName: "Code", width: 300 },
                  { field: "developmentType", headerName: "Development Type", width: 300 },
                  { field: "message", headerName: "Message", width: 800 },
                ];

                setColumnData(skuColumns);
                setFileData(skuErrorsData);
              }

              // 2. Handle Invalid Multi-select Field Errors
              else if (hasInvalidMultiSelectFieldErrors) {
                const invalidRows =
                  Object.entries(invalidMultiSelectFieldErrors ?? {}).flatMap(([itemId, messages]) =>
                    (messages ?? []).map((msg, index) => ({
                      id: `${itemId}-${index + 1}`,
                      itemId,
                      message: msg,
                    }))
                  ) ?? [];

                const invalidColumns = [
                  { field: "itemId", headerName: "Item ID", width: 180 },
                  { field: "message", headerName: "Invalid Multi-select Values", width: 900, flex: 1 },
                ];

                setColumnData(invalidColumns);
                setFileData(invalidRows);
              }

              // 3. Handle General Row Errors
              else if (errorResponse?.errors?.length > 0) {
                let errorRowData;

                if (errorResponse.message.includes("Paired CVL ")) {
                  errorRowData = rowData?.filter((_: any, index: number) =>
                    errorResponse?.errors?.includes(String(index + 1))
                  );
                } else {
                  errorRowData = rowData?.filter((item: any) =>
                    errorResponse?.errors?.includes(item?.PrjCode) ||
                    errorResponse?.errors?.includes(item?.DevelopmentType) ||
                    errorResponse?.errors?.includes(item?.Stockcode)
                  );
                }

                if (errorRowData?.length > 0) {
                  const columns = Object.keys(errorRowData[0]).map((field) => ({
                    field,
                    headerName: field,
                    width: 150,
                  }));

                  const convertedData = errorRowData.map((item, index) => ({
                    ...item,
                    id: `${item?.PrjCode}-${index + 1}`,
                  }));

                  setColumnData(columns);
                  setFileData(convertedData);
                }
              }

              fileInputRef.current.value = "";
              setErrorMessage(errorResponse?.data?.errorMessage || errorResponse?.message);
              setSelectedFile(null);
              setIsError(true);
            };

            reader.readAsArrayBuffer(selectedFile);
          }
          console.error("Exception from update file", error);
        });
    } else {
      toast.error("Upload file less than 5MB");
    }
  };

  const handleDownloadCVLFields = async () => {
    const fileDownload = await FileDownload({
      requestInput: null,
      inputFileName: 'CVLs_Values_',
      inputFileFormat: 'CVL Fields',
      setLoading,
    });
    await fileDownload.handleDownload();
  };

  return (
    <Card sx={{ marginBottom: 1 }}>
      <Loader isLoading={loading} />
      <CardContent>
        <div
          style={{
            color: "#454545",
            padding: "20px",
            borderBottom: "1px solid #ccc",
            fontSize: "20px",
            fontStyle: "normal",
            fontWeight: "400",
            lineHeight: "normal",
          }}
        >
          <p style={{ margin: "0px" }}>Bulk Product Update </p>
          {/* JDE SKU Validation Toggle Switch - only for users who can bypass */}
          {canBypassSkuValidation && (
            <div style={{ display: "flex", alignItems: "center" }}>
              <Grid item xs={5} >
                <Typography style={{ textTransform: 'capitalize', fontWeight: 500, fontSize: "14px", color: "#454545" }}>
                  JDE SKU Validation
                </Typography>
              </Grid>
              <Grid item xs={1}>
                <ToggleSwitchButton
                  checked={applySkuValidation}
                  onChange={(e) => setApplySkuValidation(e.target.checked)}
                  fieldName={"applySkuValidation"}
                />
              </Grid>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "start" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <InfoIcon sx={{ fontSize: 18, marginRight: "2px" }} />
                <p style={{ fontSize: "12px", margin: "0" }}>Notes:</p>
              </div>
              <ul style={{ fontSize: "12px", marginTop: "4px", paddingLeft: "20px" }}>
                <li>The Excel file must include a worksheet named <b>"Products"</b>; otherwise, the system will not allow it to proceed.</li>
                <li><b>PrjCode</b> is a mandatory field. The fields ShortDesc, BrandCollection, Stage, Status, Category, Function, and DevelopmentType are conditionally mandatory if those columns are included in the file.</li>
                <li><b>Dependent Fields:</b> If dependent fields contain invalid values, the system will show a validation message.
                  <ul>
                    <li><b>Status</b> depends on <b>Stage</b></li>
                    <li><b>Function</b> depends on <b>Category</b></li>
                  </ul>
                </li>
                <li>For all <b>CVL (dropdown)</b> fields, the value must match one of the allowed CVL options exactly. Any incorrect or unknown values will be replaced with blank.</li>
              </ul>
            </div>
          </div>
        </div>
        <Box sx={{ height: 1, backgroundColor: "#ccc" }} />
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            padding: "20px",
          }}
        >
          <input
            type="file"
            id="fileInput"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept=".xlsx"
          />
          <div
            className="inputWrap"
            style={{ width: "40%", marginRight: "10px", maxWidth: "100%" }}
          >
            <input
              type="text"
              name="fileName"
              id="fileName"
              value={selectedFile ? selectedFile.name : ""}
              style={{
                width: "100%",
                height: "35px",
              }}
              readOnly
            />

            <p
              style={{
                color: "#717171",
                fontSize: "15px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "normal",
                letterSpacing: "0.17px",
              }}
            >
              XLSX (max. 5MB).
              <span
                onClick={handleDownloadCVLFields}
                style={{ cursor: "pointer", paddingLeft: "10px" }}
              >
                <strong>[Download CVL Fields]</strong>
              </span>
            </p>
          </div>

          <Button
            onClick={handleBrowseClick}
            variant="contained"
            style={{
              borderRadius: "0px",
              border: "0.5px solid grey",
              width: "150px",
              height: "35px",
              marginRight: "10px",
            }}
          >
            Browse
          </Button>

          <Button
            onClick={handleSubmit}
            style={{
              borderRadius: "0px",
              border: "0.5px solid grey",
              width: "150px",
              height: "35px",
              marginRight: "10px",
            }}
            disabled={selectedFile ? false : true}
          >
            Submit
          </Button>
          <Button
            onClick={handleClear}
            style={{
              borderRadius: "0px",
              border: "0.5px solid grey",
              width: "150px",
              height: "35px",
            }}
          >
            Clear
          </Button>
        </Box>

        {isError ? (
          <>
            <div
              style={{
                color: "red",
                padding: "20px",
                borderBottom: "1px solid #ccc",
                fontSize: "20px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "normal",
              }}
            >
              <p style={{ margin: "0px" }}>{errorMessage}</p>
            </div>
            <Box className="bulk-update-grid-cell" sx={{ height: "100%", width: "100%" }}>
              {fileData.length > 0 && (
                <DataGrid
                  rows={fileData}
                  columns={columnData}
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                    },
                  }}
                  disableColumnFilter
                  disableColumnSelector
                  disableDensitySelector
                  disableRowSelectionOnClick={true}
                  getRowHeight={() => 'auto'}
                />
              )}
            </Box>
          </>
        ) : (
          ""
        )}
      </CardContent>
    </Card>
  );
};
export default BulkUpdate;
